import api from "@/app/api";
import {
  AccountsLinkDetails,
  accountsLinkService,
} from "@/app/services/generated/accountslinkService";
import {
  TenantDepositSettings,
  bottleDepositSettingsService,
} from "@/app/services/generated/bottledepositsettingsService";
import {
  CompanySettings,
  companySettingsService,
} from "@/app/services/generated/companysettingsService";
import {
  DeliveryNotificationSettings,
  deliveryNotificationService,
} from "@/app/services/generated/deliverynotificationService";
import {
  KegstarSettings,
  kegstarLinkService,
} from "@/app/services/generated/kegstarlinkService";
import type {
  DateAndTimeConfiguration,
  CurrencyConfiguration,
  LanguageAndCultureConfiguration,
  NumberConfiguration,
} from "@/app/services/generated/tenantService";
import { tenantService } from "@/app/services/generated/tenantService";
import { reactive, shallowReadonly, watch } from "vue";
import userStore from "@/store/modules/userStore";
import {
  TenantSettings,
  tenantSettingsService,
} from "@/app/services/generated/tenantsettingsService";
import type { EnumPurchaseOrderDeliveryDisbursement } from "@/app/services/generated/enums/enumPurchaseOrderDeliveryDisbursement.generated";
import type { EnumCostPriceUpdateType } from "@/app/services/generated/enums/enumCostPriceUpdateType.generated";

interface TenantStoreState {
  companySettings: Readonly<CompanySettings>;
  bottleDepositSettings: Readonly<TenantDepositSettings>;
  deliveryNotificationSettings: Readonly<DeliveryNotificationSettings>;
  kegstarSettings: Readonly<KegstarSettings>;
  accountsLinkDetails: Readonly<AccountsLinkDetails>;
  tenantSettings: Readonly<TenantSettings>;
}

const state = reactive<TenantStoreState>({
  companySettings: new CompanySettings(),
  bottleDepositSettings: new TenantDepositSettings(),
  deliveryNotificationSettings: new DeliveryNotificationSettings(),
  kegstarSettings: new KegstarSettings(),
  accountsLinkDetails: new AccountsLinkDetails(),
  tenantSettings: new TenantSettings(),
});

async function refreshStore() {
  [
    state.companySettings,
    state.bottleDepositSettings,
    state.deliveryNotificationSettings,
    state.kegstarSettings,
    state.accountsLinkDetails,
    state.tenantSettings,
  ] = await Promise.all([
    companySettingsService.getCompanySettings(api.tenantId),
    bottleDepositSettingsService.getTenantSettings(api.tenantId),
    deliveryNotificationService.getDeliveryNotificationSettings(api.tenantId),
    kegstarLinkService.getKegstarSettings(api.tenantId),
    accountsLinkService.getAccountsLinkDetails(api.tenantId),
    tenantSettingsService.getTenantSettings(api.tenantId),
  ]);
}

// We should only need to refresh our store completely when the tenant as a whole has changed, otherwise our individual
// save calls should catch and individually replace.
watch(
  () => userStore.state.tenant,
  async (newTenant) => {
    if (newTenant) {
      await refreshStore();
    }
  },
  { immediate: true }
);

const saveDateAndTimeConfiguration = async (
  dateAndTimeConfiguration: DateAndTimeConfiguration
) => {
  await tenantService.updateDateAndTimeConfiguration(
    api.tenantId,
    dateAndTimeConfiguration
  );

  // Stored as part of the tenant, which is in the userStore
  await userStore.refreshStore();
};

const saveCurrencyConfiguration = async (
  currencyConfiguration: CurrencyConfiguration
) => {
  await tenantService.updateCurrencyConfiguration(
    api.tenantId,
    currencyConfiguration
  );

  // Stored as part of the tenant, which is in the userStore
  await userStore.refreshStore();
};

const saveNumberConfiguration = async (
  numberConfiguration: NumberConfiguration
) => {
  await tenantService.updateNumberConfiguration(
    api.tenantId,
    numberConfiguration
  );

  // Stored as part of the tenant, which is in the userStore
  await userStore.refreshStore();
};

const saveLanguageAndCultureConfiguration = async (
  languageAndCultureConfiguration: LanguageAndCultureConfiguration
) => {
  await tenantService.updateLanguageAndCultureConfiguration(
    api.tenantId,
    languageAndCultureConfiguration
  );

  // Stored as part of the tenant, which is in the userStore
  await userStore.refreshStore();
};

const saveCompanySettings = async (companySettings: CompanySettings) => {
  await companySettingsService.updateCompanySettings(
    api.tenantId,
    companySettings
  );

  state.companySettings = await companySettingsService.getCompanySettings(
    api.tenantId
  );
};

const saveTenantBottleDepositSettings = async (
  bottleDepositSettings: TenantDepositSettings
) => {
  await bottleDepositSettingsService.saveTenantSettings(
    api.tenantId,
    bottleDepositSettings
  );

  state.bottleDepositSettings =
    await bottleDepositSettingsService.getTenantSettings(api.tenantId);
};

const saveDeliveryNotificationSettings = async (
  deliveryNotificationSettings: DeliveryNotificationSettings
) => {
  await deliveryNotificationService.saveDeliveryNotificationSettings(
    api.tenantId,
    deliveryNotificationSettings
  );

  state.deliveryNotificationSettings =
    await deliveryNotificationService.getDeliveryNotificationSettings(
      api.tenantId
    );
};

const saveKegstarSettings = async (kegstarSettings: KegstarSettings) => {
  await kegstarLinkService.saveKegstarSettings(api.tenantId, kegstarSettings);

  state.kegstarSettings = await kegstarLinkService.getKegstarSettings(
    api.tenantId
  );
};

const setIsMegaBatchesEnabled = async (isMegaBatchesEnabled: boolean) => {
  await tenantSettingsService.setIsMegaBatchesEnabled(
    api.tenantId,
    isMegaBatchesEnabled
  );

  state.tenantSettings = await tenantSettingsService.getTenantSettings(
    api.tenantId
  );
};

const saveProductBatchDuration = async (productBatchDuration: number) => {
  await tenantSettingsService.setProductBatchDuration(
    api.tenantId,
    productBatchDuration
  );

  state.tenantSettings = await tenantSettingsService.getTenantSettings(
    api.tenantId
  );
};

const savePurchaseOrderSettings = async (
  poDefaultDeliveryDisbursement: EnumPurchaseOrderDeliveryDisbursement
) => {
  await tenantSettingsService.savePurchaseOrderSettings(
    api.tenantId,
    poDefaultDeliveryDisbursement
  );

  state.tenantSettings = await tenantSettingsService.getTenantSettings(
    api.tenantId
  );
};

const saveProductionSettings = async (
  usesHopAlpha: boolean,
  costPriceUpdateType: EnumCostPriceUpdateType
) => {
  await tenantSettingsService.saveProductionSettings(
    api.tenantId,
    usesHopAlpha,
    costPriceUpdateType
  );

  state.tenantSettings = await tenantSettingsService.getTenantSettings(
    api.tenantId
  );
};

const saveTaxSettings = async (usesRegionalTax: boolean) => {
  await tenantSettingsService.saveTaxSettings(api.tenantId, usesRegionalTax);

  state.tenantSettings = await tenantSettingsService.getTenantSettings(
    api.tenantId
  );
};

const saveDisplaySettings = async (
  showTenantIndicator: boolean,
  tenantIndicatorColour: string,
  tenantIndicatorText: string
) => {
  await tenantSettingsService.saveDisplaySettings(
    api.tenantId,
    showTenantIndicator,
    tenantIndicatorColour,
    tenantIndicatorText
  );

  state.tenantSettings = await tenantSettingsService.getTenantSettings(
    api.tenantId
  );
};

export default {
  // Using readonly breaks enums as it wraps with proxies.
  state: shallowReadonly(state),

  saveDateAndTimeConfiguration,
  saveCurrencyConfiguration,
  saveNumberConfiguration,
  saveCompanySettings,
  saveTenantBottleDepositSettings,
  saveDeliveryNotificationSettings,
  saveKegstarSettings,
  saveLanguageAndCultureConfiguration,
  setIsMegaBatchesEnabled,
  saveProductBatchDuration,
  savePurchaseOrderSettings,
  saveProductionSettings,
  saveTaxSettings,
  saveDisplaySettings,
};
